/**
 * Refresh Token Service
 * Handles token refresh logic with retry mechanism and cooldown
 */

import { refreshToken } from '../api/auth';
import { getStoredTokens, clearTokens } from '../utils/storage';
import { isTokenExpired } from '../utils/tokenUtils';

export interface RefreshTokenState {
  refreshRetryCount: number;
  lastRefreshAttempt: number;
}

export interface RefreshTokenConfig {
  maxRetries: number;
  cooldownMs: number;
}

const DEFAULT_CONFIG: RefreshTokenConfig = {
  maxRetries: 3,
  cooldownMs: 5 * 60 * 1000, // 5 minutes
};

export class RefreshTokenService {
  private state: RefreshTokenState = {
    refreshRetryCount: 0,
    lastRefreshAttempt: 0,
  };

  private config: RefreshTokenConfig;

  constructor(config: Partial<RefreshTokenConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Reset retry state
   */
  resetRetryState(): void {
    this.state = {
      refreshRetryCount: 0,
      lastRefreshAttempt: 0,
    };
  }

  /**
   * Get current retry state
   */
  getRetryState(): RefreshTokenState {
    return { ...this.state };
  }

  /**
   * Check if we're in cooldown period
   */
  isInCooldown(): boolean {
    if (this.state.refreshRetryCount < this.config.maxRetries) {
      return false;
    }

    const timeSinceLastAttempt = Date.now() - this.state.lastRefreshAttempt;
    return timeSinceLastAttempt < this.config.cooldownMs;
  }

  /**
   * Attempt to refresh tokens
   */
  async refreshTokens(): Promise<{ success: boolean; error?: string }> {
    // Check if we're in cooldown
    if (this.isInCooldown()) {
      return { success: false, error: 'In cooldown period' };
    }

    // Reset retry count if cooldown has passed
    if (this.state.refreshRetryCount >= this.config.maxRetries) {
      this.resetRetryState();
    }

    try {
      const storedTokens = getStoredTokens();
      if (!storedTokens) {
        return { success: false, error: 'No stored tokens' };
      }

      // Note: We don't check refresh token expiry locally since it's not a JWT
      // The server will validate the refresh token when we try to use it

      // Attempt refresh
      const response = await refreshToken({
        user_id: storedTokens.user_id,
        refresh_token: storedTokens.refresh_token,
        client_id: "influencer-app",
      });

      // Store new tokens
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      if (response.expires_at) {
        localStorage.setItem('expires_at', response.expires_at.toString());
      }

      // Reset retry state on success
      this.resetRetryState();

      return { success: true };

    } catch (error) {
      // Increment retry count
      this.state.refreshRetryCount += 1;
      this.state.lastRefreshAttempt = Date.now();

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // If the server says the refresh token is invalid/expired, we should logout
      if (errorMessage.includes('invalid') || errorMessage.includes('expired') || errorMessage.includes('unauthorized') || 
          errorMessage.includes('ERROR_CODE_UNAUTHENTICATED_INVALID_TOKEN')) {
        
        // Clear tokens immediately to prevent further attempts
        clearTokens();
        
        return { 
          success: false, 
          error: 'Refresh token invalid or expired' 
        };
      }

      return { 
        success: false, 
        error: errorMessage
      };
    }
  }

  /**
   * Force logout (clear tokens and reset state)
   */
  forceLogout(): void {
    clearTokens();
    this.resetRetryState();
  }

  /**
   * Stop all refresh attempts (for logout scenarios)
   */
  stopAllRefreshAttempts(): void {
    this.state.refreshRetryCount = this.config.maxRetries; // Force into cooldown
    this.state.lastRefreshAttempt = Date.now();
  }

  /**
   * Debug method to expose current state
   */
  getDebugInfo(): { state: RefreshTokenState; config: RefreshTokenConfig; isInCooldown: boolean } {
    return {
      state: this.getRetryState(),
      config: this.config,
      isInCooldown: this.isInCooldown()
    };
  }
}

// Export singleton instance
export const refreshTokenService = new RefreshTokenService();

// Expose debug info to window for testing
if (typeof window !== 'undefined') {
  (window as any).refreshTokenService = refreshTokenService;
}
